import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Form, Typography } from '../../../base';
import SettingsTabs from '../SettingsTabs';
import { StyledHeader } from './SettingsContent.styles';
import { useTranslation } from 'react-i18next';
import { defaultNotificationsSettingsFormValues } from '../../../common';
import {
    organizationActions,
    organizationSelectors,
} from '../../../actions/organization/redux';
import PargodaLoading from '../../PargodaLoading';

interface ISettingsFormData {
    companyName: string;
    companyId: string;
    phoneNumber: string;
    hqAddress: string;
    companyWebsite: string;
    recruitingEmailAddress: string;
    companyDescription: string;
    companyLogo: string | File | null;
    notificationsSettings: Record<string, { sms: boolean; email: boolean }>;
    primaryColor: string | null;
    secondaryColor: string | null;
}

export const SettingsContent = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const companySettings = useSelector(
        organizationSelectors.getCompanySettings
    );
    const isLoading = useSelector(
        organizationSelectors.getIsGetCompanySettingsLoading
    );
    const isUpdating = useSelector(
        organizationSelectors.getIsUpdateCompanySettingsLoading
    );

    const methods = useForm<ISettingsFormData>({
        // TODO: add validation schema
        defaultValues: {
            companyName: '',
            companyId: '',
            phoneNumber: '',
            hqAddress: '',
            companyWebsite: '',
            recruitingEmailAddress: '',
            companyDescription: '',
            companyLogo: null as string | File | null,
            notificationsSettings: defaultNotificationsSettingsFormValues,
            primaryColor: null,
            secondaryColor: null,
        },
    });

    const { control, handleSubmit, reset } = methods;

    // Fetch company settings on mount
    useEffect(() => {
        dispatch(organizationActions.fetchCompanySettings());
    }, [dispatch]);

    // Update form values when company settings are loaded
    useEffect(() => {
        if (companySettings?.companyInfo) {
            const { companyInfo } = companySettings;
            reset({
                companyName: companyInfo.name || '',
                companyId: companyInfo.key || '',
                phoneNumber: companyInfo.phone || '',
                hqAddress: companyInfo.addressText || '',
                companyWebsite: companyInfo.url || '',
                recruitingEmailAddress: '', // TODO: This field needs to be added to the server model
                companyDescription: companyInfo.description || '',
                companyLogo: companyInfo.companyLogoUrl || null,
                notificationsSettings: defaultNotificationsSettingsFormValues,
                primaryColor: companySettings.primaryColor || null,
                secondaryColor: companySettings.secondaryColor || null,
            });
        }
    }, [companySettings, reset]);

    const onSubmit = (data: ISettingsFormData) => {
        dispatch(
            organizationActions.updateCompanySettings({
                description: data.companyDescription,
                phoneNumber: data.phoneNumber,
                address: data.hqAddress,
                website: data.companyWebsite,
                logoFile:
                    data.companyLogo instanceof File ? data.companyLogo : null,
                primaryColor: data.primaryColor || undefined,
                secondaryColor: data.secondaryColor || undefined,
            })
        );
    };
    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                }}
            >
                <PargodaLoading />
            </Box>
        );
    }

    return (
        <>
            <StyledHeader>
                <Typography variant="h4">{t('settings.title')}</Typography>
                <Box>
                    <Button variant="outlined" sx={{ mr: 2 }}>
                        {t('settings.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isUpdating}
                    >
                        {isUpdating ? t('common.loading') : t('settings.save')}
                    </Button>
                </Box>
            </StyledHeader>
            <Form methods={methods} onSubmit={onSubmit}>
                <SettingsTabs control={control} />
            </Form>
        </>
    );
};
